/**
 * Typed API client — the single entry point the web package uses to reach
 * the Fastify API. Every call validates the response against the shared
 * zod schemas exported from `@isms/shared`, so a schema drift between
 * server and client surfaces as a `ResponseValidationError` at the call
 * site rather than an undefined-property error in component render.
 *
 * Error handling is uniform: non-2xx responses are parsed with
 * `apiErrorResponseSchema` and thrown as `ApiError`, carrying the stable
 * `code` string so callers can branch on known failure modes (e.g.
 * `invalid_code` on OTP verify) while falling back to `message` for
 * anything unexpected.
 *
 * The client sends `credentials: 'include'` on every request so the
 * session cookie (admin or volunteer) flows both ways without the caller
 * having to know which kind of session is active.
 */

import {
  adminSignInRequestSchema,
  adminSignInResponseSchema,
  adminSignInVerifyCodeRequestSchema,
  adminSignInVerifyCodeResponseSchema,
  adminSignInTotpRequestSchema,
  adminSignInTotpResponseSchema,
  adminTotpEnrolConfirmRequestSchema,
  adminTotpEnrolConfirmResponseSchema,
  adminTotpResetRequestSchema,
  adminTotpResetResponseSchema,
  createDocumentRequestSchema,
  documentSchema,
  documentPdfUrlResponseSchema,
  documentsListQuerySchema,
  documentsListResponseSchema,
  archiveDocumentResponseSchema,
  unarchiveDocumentResponseSchema,
  requestDocumentUploadUrlRequestSchema,
  requestDocumentUploadUrlResponseSchema,
  adminDashboardResponseSchema,
  adminMeResponseSchema,
  adminTotpEnrolResponseSchema,
  adminUsersListResponseSchema,
  apiErrorResponseSchema,
  complianceRequirementSchema,
  complianceRequirementsListResponseSchema,
  createAdminUserRequestSchema,
  createAdminUserResponseSchema,
  createComplianceRequirementRequestSchema,
  getAdminUserResponseSchema,
  updateAdminUserRequestSchema,
  updateAdminUserResponseSchema,
  updateComplianceRequirementRequestSchema,
  bulkInvalidateRequirementRequestSchema,
  bulkInvalidateRequirementResponseSchema,
  archivePersonRequestSchema,
  archivePersonResponseSchema,
  createPersonRequestSchema,
  createPersonResponseSchema,
  updatePersonRequestSchema,
  personSchema,
  attachEvidenceToRecordRequestSchema,
  attachEvidenceToRecordResponseSchema,
  complianceRecordDetailResponseSchema,
  complianceRecordsListQuerySchema,
  complianceRecordsListResponseSchema,
  confirmUploadRequestSchema,
  confirmUploadResponseSchema,
  cronHealthResponseSchema,
  cronTriggerResponseSchema,
  mailFailuresListResponseSchema,
  mailFailureRetryResponseSchema,
  mailFailureDeleteResponseSchema,
  stuckAuditPacksResponseSchema,
  exportAuditPackRequestSchema,
  exportAuditPackResponseSchema,
  exportListResponseSchema,
  meAttestationConfirmResponseSchema,
  meDashboardResponseSchema,
  meEvidenceConfirmRequestSchema,
  meEvidenceConfirmResponseSchema,
  meEvidenceRequestUrlRequestSchema,
  meEvidenceRequestUrlResponseSchema,
  meIntakeSubmitRequestSchema,
  meIntakeSubmitResponseSchema,
  meProfileUpdateRequestSchema,
  meProfileUpdateResponseSchema,
  mePendingAckListResponseSchema,
  meAckStartRequestSchema,
  meAckStartResponseSchema,
  meAckStepUpRequestSchema,
  meAckStepUpResponseSchema,
  meAckConfirmRequestSchema,
  meAckConfirmResponseSchema,
  markNqaItsSyncedRequestSchema,
  markNqaItsSyncedResponseSchema,
  peopleListResponseSchema,
  personDetailResponseSchema,
  personHistoryResponseSchema,
  requestUploadUrlRequestSchema,
  requestUploadUrlResponseSchema,
  requestReverificationRequestSchema,
  requestReverificationResponseSchema,
  complianceRecordSchema,
  supersedeComplianceRecordRequestSchema,
  verifyComplianceRecordRequestSchema,
  verifyComplianceRecordResponseSchema,
  volunteerSignInRequestSchema,
  volunteerSignInResponseSchema,
  volunteerSignInVerifyRequestSchema,
  volunteerSignInVerifyResponseSchema,
  type AdminSignInRequest,
  type AdminSignInResponse,
  type AdminSignInVerifyCodeRequest,
  type AdminSignInVerifyCodeResponse,
  type AdminSignInTotpRequest,
  type AdminSignInTotpResponse,
  type AdminTotpEnrolConfirmRequest,
  type AdminTotpEnrolConfirmResponse,
  type AdminTotpResetRequest,
  type AdminTotpResetResponse,
  type AdminDashboardResponse,
  type AdminMeResponse,
  type AdminTotpEnrolResponse,
  type AdminUsersListResponse,
  type ApiErrorResponse,
  type ComplianceRequirement,
  type ComplianceRequirementsListResponse,
  type CreateAdminUserRequest,
  type CreateAdminUserResponse,
  type CreateComplianceRequirementRequest,
  type GetAdminUserResponse,
  type PaginationParams,
  type UpdateAdminUserRequest,
  type UpdateAdminUserResponse,
  type UpdateComplianceRequirementRequest,
  type BulkInvalidateRequirementRequest,
  type BulkInvalidateRequirementResponse,
  type ArchivePersonRequest,
  type ArchivePersonResponse,
  type CreatePersonRequest,
  type CreatePersonResponse,
  type UpdatePersonRequest,
  type Person,
  type AttachEvidenceToRecordRequest,
  type AttachEvidenceToRecordResponse,
  type ComplianceRecordDetailResponse,
  type ComplianceRecordsListQuery,
  type ComplianceRecordsListResponse,
  type ConfirmUploadRequest,
  type ConfirmUploadResponse,
  type CreateDocumentRequest,
  type Document,
  type DocumentPdfUrlResponse,
  type DocumentsListQuery,
  type DocumentsListResponse,
  type ArchiveDocumentResponse,
  type UnarchiveDocumentResponse,
  type RequestDocumentUploadUrlRequest,
  type RequestDocumentUploadUrlResponse,
  type CronHealthResponse,
  type CronTriggerResponse,
  type MailFailuresListResponse,
  type MailFailureRetryResponse,
  type MailFailureDeleteResponse,
  type StuckAuditPacksResponse,
  type ExportAuditPackRequest,
  type ExportAuditPackResponse,
  type ExportListResponse,
  type MeDashboardResponse,
  type MeEvidenceConfirmRequest,
  type MeEvidenceConfirmResponse,
  type MeEvidenceRequestUrlRequest,
  type MeEvidenceRequestUrlResponse,
  type MeIntakeSubmitRequest,
  type MeIntakeSubmitResponse,
  type MeAttestationConfirmResponse,
  type MeProfileUpdateRequest,
  type MeProfileUpdateResponse,
  type MePendingAckListResponse,
  type MeAckStartRequest,
  type MeAckStartResponse,
  type MeAckStepUpRequest,
  type MeAckStepUpResponse,
  type MeAckConfirmRequest,
  type MeAckConfirmResponse,
  type MarkNqaItsSyncedRequest,
  type MarkNqaItsSyncedResponse,
  type PeopleListQuery,
  type PeopleListResponse,
  type PersonDetailResponse,
  type PersonHistoryResponse,
  type RequestUploadUrlRequest,
  type RequestUploadUrlResponse,
  type RequestReverificationRequest,
  type RequestReverificationResponse,
  type ComplianceRecord,
  type SupersedeComplianceRecordRequest,
  type VerifyComplianceRecordRequest,
  type VerifyComplianceRecordResponse,
  type VolunteerSignInRequest,
  type VolunteerSignInResponse,
  type VolunteerSignInVerifyRequest,
  type VolunteerSignInVerifyResponse,
} from '@isms/shared';
import { z } from 'zod';

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: Record<string, unknown> | null;
  readonly requestId: string;

  constructor(status: number, body: ApiErrorResponse) {
    super(body.error.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.error.code;
    this.details = body.error.details;
    this.requestId = body.request_id;
    // Log to the console so the request id ends up in screenshots taken
    // with DevTools open and in any session-recording captures the user
    // sends to support — independently of whether the surface that
    // catches the error renders the id.
    console.error('ApiError', {
      status: this.status,
      code: this.code,
      requestId: this.requestId,
      message: this.message,
    });
  }
}

/**
 * Thrown when the server returned 2xx but the response body fails to
 * parse against the expected schema — indicates a deploy-time drift
 * between `packages/shared` versions running in the API and the web
 * client. Kept distinct from `ApiError` so monitoring can page on it
 * separately.
 */
export class ResponseValidationError extends Error {
  readonly issues: z.ZodIssue[];

  constructor(issues: z.ZodIssue[]) {
    super(`API response failed schema validation: ${issues.map((i) => i.message).join('; ')}`);
    this.name = 'ResponseValidationError';
    this.issues = issues;
  }
}

export interface ApiClientOptions {
  /** Base URL for API calls. Defaults to '' (same-origin, so Vite's dev
   *  proxy handles it locally and the production build hits the same
   *  hostname the SPA was served from). */
  baseUrl?: string;
  /** Injected fetch for tests. Defaults to the global `fetch`. */
  fetchImpl?: typeof fetch;
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;

  constructor(options: ApiClientOptions = {}) {
    this.baseUrl = options.baseUrl ?? '';
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  /**
   * Step 1 of volunteer sign-in. Sends an email; the API emails a
   * 6-digit OTP and returns 200, or 404 `email_not_registered` if the
   * email is not on file. Callers should branch on the 404 to show
   * "contact your school admin" copy.
   */
  async volunteerSignIn(request: VolunteerSignInRequest): Promise<VolunteerSignInResponse> {
    const parsedRequest = volunteerSignInRequestSchema.parse(request);
    return this.post('/volunteer/sign-in', parsedRequest, volunteerSignInResponseSchema);
  }

  /**
   * Step 2 of volunteer sign-in. Submits the email + OTP; on success the
   * server sets the `isms_volunteer_session` cookie and returns the
   * minimal profile for the `/me` redirect. All failures collapse to
   * `invalid_code` — do not try to distinguish bad-code / expired-code /
   * unknown-email in the UI.
   */
  async volunteerSignInVerify(
    request: VolunteerSignInVerifyRequest,
  ): Promise<VolunteerSignInVerifyResponse> {
    const parsedRequest = volunteerSignInVerifyRequestSchema.parse(request);
    return this.post(
      '/volunteer/sign-in/verify',
      parsedRequest,
      volunteerSignInVerifyResponseSchema,
    );
  }

  /** Volunteer sign-out — clears the session cookie server-side. */
  async volunteerSignOut(): Promise<void> {
    await this.requestRaw('POST', '/volunteer/sign-out', null);
  }

  /**
   * `GET /me` — volunteer dashboard projection. Behind
   * `requireVolunteerSession()` server-side: a 401 response means the
   * session cookie is missing / revoked / expired and the UI should
   * redirect back to `/sign-in`.
   */
  async getMe(): Promise<MeDashboardResponse> {
    return this.get('/me', meDashboardResponseSchema);
  }

  /**
   * `POST /me/evidence` — request a pre-signed PUT URL for volunteer
   * self-service upload. The server verifies the record belongs to the
   * session's person before issuing the URL.
   */
  async requestMeEvidenceUpload(
    request: MeEvidenceRequestUrlRequest,
  ): Promise<MeEvidenceRequestUrlResponse> {
    const parsedRequest = meEvidenceRequestUrlRequestSchema.parse(request);
    return this.post('/me/evidence', parsedRequest, meEvidenceRequestUrlResponseSchema);
  }

  /**
   * PUTs a file at a pre-signed URL. Not routed through the JSON
   * request/response envelope — the URL is S3/MinIO-direct and returns a
   * 200 with an empty body on success. Kept on the client so the route
   * component doesn't need a second `fetch` reference.
   */
  async putEvidenceFile(uploadUrl: string, file: File): Promise<void> {
    const response = await this.fetchImpl(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type },
    });
    if (!response.ok) {
      throw new Error(`Upload failed with status ${response.status}.`);
    }
  }

  /**
   * `POST /me/evidence/confirm` — the server fetches the object, hashes
   * it, validates MIME + size, writes the `evidence` row, links the
   * record, and transitions it to `submitted` (PRD 04 §Self-service portal
   * step 5). All atomic.
   */
  async confirmMeEvidence(request: MeEvidenceConfirmRequest): Promise<MeEvidenceConfirmResponse> {
    const parsedRequest = meEvidenceConfirmRequestSchema.parse(request);
    return this.post('/me/evidence/confirm', parsedRequest, meEvidenceConfirmResponseSchema);
  }

  /**
   * `PATCH /me/profile` — volunteer self-edits their own contact + name
   * + address fields from `/me` (M12, PRD 04 §Volunteer self-service).
   * Behind `requireVolunteerSession()` server-side: a 401 means the
   * session is gone and the SPA should redirect to `/sign-in`. A 400
   * (`patch must update at least one field`, malformed postcode,
   * empty-string name part) is surfaced inline next to the form.
   * `contact_email` / `date_of_birth` / `start_date` / `status` and the
   * NQA-only admin fields are stripped server-side and cannot be set
   * here; submitting them is silently no-op (zod strip-unknown).
   */
  async patchMeProfile(request: MeProfileUpdateRequest): Promise<MeProfileUpdateResponse> {
    const parsedRequest = meProfileUpdateRequestSchema.parse(request);
    return this.patch('/me/profile', parsedRequest, meProfileUpdateResponseSchema);
  }

  /**
   * `POST /me/attestations/:requirementId/confirm` — volunteer satisfies
   * a `pattern='volunteer_attestation'` requirement by clicking the
   * configured confirm button on `/me`. No request body. The server
   * lazy-creates a `compliance_records` row in `verified` status (or
   * flips an existing pending/expired one) and writes a paired
   * `audit_events` row. 409 (`already_verified`) on rows that are
   * already verified-and-current.
   */
  async confirmMeAttestation(requirementId: string): Promise<MeAttestationConfirmResponse> {
    const path = `/me/attestations/${encodeURIComponent(requirementId)}/confirm`;
    return this.post(path, null, meAttestationConfirmResponseSchema);
  }

  /**
   * Step 1 of admin sign-in. POSTs `{ email }` to `/admin/sign-in`. The
   * server emails a 6-digit code if the address matches an active
   * `admin_users` row and returns the same generic enqueue message
   * either way (enumeration-safe — the response shape is identical on
   * hit, miss, and rate-limit). PRD 04 §Identity and access.
   */
  async adminSignIn(request: AdminSignInRequest): Promise<AdminSignInResponse> {
    const parsedRequest = adminSignInRequestSchema.parse(request);
    return this.post('/admin/sign-in', parsedRequest, adminSignInResponseSchema);
  }

  /**
   * Step 2 of admin sign-in. POSTs `{ email, code }` to
   * `/admin/sign-in/verify-code`. On success the response is a
   * discriminated union:
   *   - `{ challenge: 'totp', mfa_token, expires_at }` when the admin
   *     has TOTP enrolled — pass `mfa_token` to `adminSignInTotp` next.
   *   - `{ challenge: 'totp_enrolment_required', enrol_token,
   *     expires_at, otpauth_uri, secret_base32 }` for the forced
   *     first-login enrolment branch — pass `enrol_token` to
   *     `adminTotpEnrolConfirm` after the admin scans the QR.
   * All failures collapse to `invalid_code`.
   */
  async adminSignInVerifyCode(
    request: AdminSignInVerifyCodeRequest,
  ): Promise<AdminSignInVerifyCodeResponse> {
    const parsedRequest = adminSignInVerifyCodeRequestSchema.parse(request);
    return this.post(
      '/admin/sign-in/verify-code',
      parsedRequest,
      adminSignInVerifyCodeResponseSchema,
    );
  }

  /**
   * Step 3 of admin sign-in (TOTP path). POSTs `{ mfa_token, totp_code }`
   * to `/admin/sign-in/totp`. On success the API sets the
   * `isms_admin_session` cookie and returns the admin profile so the
   * SPA can render its header without a follow-up `/admin/me`. Bad
   * code / expired token / disabled account all collapse to
   * `mfa_failed`.
   */
  async adminSignInTotp(request: AdminSignInTotpRequest): Promise<AdminSignInTotpResponse> {
    const parsedRequest = adminSignInTotpRequestSchema.parse(request);
    return this.post('/admin/sign-in/totp', parsedRequest, adminSignInTotpResponseSchema);
  }

  /**
   * Forced-enrolment confirm. POSTs `{ enrol_token, totp_code }` to
   * `/admin/totp/enrol-confirm`. The server verifies the code against
   * the freshly minted secret carried in the enrol token, persists
   * `mfa_secret` on the row, and mints the admin session cookie.
   * Failures collapse to `enrol_failed`.
   */
  async adminTotpEnrolConfirm(
    request: AdminTotpEnrolConfirmRequest,
  ): Promise<AdminTotpEnrolConfirmResponse> {
    const parsedRequest = adminTotpEnrolConfirmRequestSchema.parse(request);
    return this.post(
      '/admin/totp/enrol-confirm',
      parsedRequest,
      adminTotpEnrolConfirmResponseSchema,
    );
  }

  /**
   * Admin sign-out — clears the `isms_admin_session` cookie server-side.
   * Idempotent: a stale or missing cookie still returns 204.
   */
  async adminLogout(): Promise<void> {
    await this.requestRaw('POST', '/admin/logout', null);
  }

  /**
   * `GET /admin/me` — admin SPA hydrate-after-reload endpoint. A 401
   * means the cookie is gone or expired and the SPA should redirect to
   * `/admin/sign-in`.
   */
  async getAdminMe(): Promise<AdminMeResponse> {
    return this.get('/admin/me', adminMeResponseSchema);
  }

  /**
   * `GET /admin/dashboard` — one-call hydrate for the admin landing page
   * (M13, PRD 06 §Dashboard). Returns the three compliance snapshots
   * (new this week / expiring in 30 / expired-and-overdue / stood down),
   * the NQA ITS "due this week or overdue" snapshot, per-job worker
   * health, and the current dead-letter count. 401 → redirect to
   * `/admin/sign-in`. Every authenticated admin can read it.
   */
  async getAdminDashboard(): Promise<AdminDashboardResponse> {
    return this.get('/admin/dashboard', adminDashboardResponseSchema);
  }

  /**
   * `GET /people` — paginated admin list of people with optional status /
   * person_type / substring-match filters (M7 + M13 people-list slice).
   * Every authenticated admin can call this; a 401 from the API is the
   * SPA's signal to redirect the caller. Query parameters that are
   * `undefined` are omitted entirely so the server sees default
   * pagination / no filters rather than empty-string values that would
   * coerce-fail.
   */
  async adminListPeople(query: Partial<PeopleListQuery> = {}): Promise<PeopleListResponse> {
    const path = `/people${buildQueryString(query)}`;
    return this.get(path, peopleListResponseSchema);
  }

  /**
   * `POST /people` — admin person-creation. `initial_status:
   * 'onboarding'` triggers the server-side intake-token + intake-invite
   * email cascade in the same transaction; the response carries the
   * issued `intake_token_jti` so the SPA can confirm an email went
   * out. `initial_status: 'active'` skips intake.
   *
   * Known error codes the UI branches on:
   *   - 409 `email_in_use` — another live person already uses the email.
   */
  async adminCreatePerson(request: CreatePersonRequest): Promise<CreatePersonResponse> {
    const parsedRequest = createPersonRequestSchema.parse(request);
    return this.post('/people', parsedRequest, createPersonResponseSchema);
  }

  /**
   * `GET /people/:personId` — full admin person detail including the
   * compliance records and open NQA ITS task count (M7). 404 is
   * surfaced as `person_not_found` ApiError; the SPA branches on it.
   */
  async getAdminPerson(personId: string): Promise<PersonDetailResponse> {
    return this.get(`/people/${encodeURIComponent(personId)}`, personDetailResponseSchema);
  }

  /**
   * `GET /people/:personId/history` — combined reminders + audit-event
   * timeline for the admin person-detail page (PRD 06 §People list —
   * detail view). `limit` defaults to 100 server-side; the response
   * carries `truncated: true` when the underlying union exceeded the
   * cap so the UI can hint that older history exists. 404 is surfaced
   * as `person_not_found`.
   */
  async getAdminPersonHistory(
    personId: string,
    options: { limit?: number } = {},
  ): Promise<PersonHistoryResponse> {
    const qs = options.limit != null ? `?limit=${encodeURIComponent(String(options.limit))}` : '';
    return this.get(
      `/people/${encodeURIComponent(personId)}/history${qs}`,
      personHistoryResponseSchema,
    );
  }

  /**
   * `POST /people/:personId/archive` — single-step volunteer archival
   * (US-ADM-10). Flips the person to `inactive`, archives every open
   * compliance record, revokes active volunteer sessions, and
   * invalidates unused magic-link tokens — all inside one DB
   * transaction. The same UPDATE bumps `people.updated_at`, which the
   * dashboard's "needs NQA ITS sync" predicate watches. `reason` is
   * required and lands in the paired audit row's `after.reason`.
   *
   * Known error codes the UI branches on:
   *   - 404 `person_not_found` — stale person id.
   *   - 409 `already_inactive` — the person was archived between load and click.
   */
  async archiveAdminPerson(
    personId: string,
    request: ArchivePersonRequest,
  ): Promise<ArchivePersonResponse> {
    const parsedRequest = archivePersonRequestSchema.parse(request);
    return this.post(
      `/people/${encodeURIComponent(personId)}/archive`,
      parsedRequest,
      archivePersonResponseSchema,
    );
  }

  /**
   * `PATCH /people/:personId` — partial update.
   * At least one field is required or the client-boundary zod check
   * rejects before any network call (the server enforces the same rule
   * via `validation_failed`). Changing `contact_email` triggers the
   * server-side email-change cascade: every active `volunteer_sessions`
   * row is revoked and every unused `magic_link_tokens` row is
   * invalidated inside the same DB transaction (US-ADM-11, PRD 04
   * §Self-service portal — "Email change protection"). NQA-relevant
   * field changes bump `people.updated_at`, surfacing the person on the
   * "needs NQA ITS sync" dashboard panel.
   *
   * Known error codes the UI branches on:
   *   - 404 `person_not_found` — stale person id.
   *   - 409 `email_in_use` — another live person already uses the email.
   */
  async updateAdminPerson(personId: string, request: UpdatePersonRequest): Promise<Person> {
    const parsedRequest = updatePersonRequestSchema.parse(request);
    return this.patch(`/people/${encodeURIComponent(personId)}`, parsedRequest, personSchema);
  }

  /**
   * `GET /compliance-records` — paginated admin triage list with the
   * `view` filter surfaced on the dashboard drill-down links (US-ADM-06).
   * Rows are enriched with the inline `person` + `item_type` projections
   * the list UI renders. Pass a `view` from the stable
   * `expiring_in_30` / `expired_overdue` / `new_this_week` /
   * `awaiting_verification` set; fall back to `status` / `person_id` /
   * `requirement_id` for narrower slices. 401 → redirect.
   */
  async adminListComplianceRecords(
    query: Partial<ComplianceRecordsListQuery> = {},
  ): Promise<ComplianceRecordsListResponse> {
    const parsedQuery = complianceRecordsListQuerySchema.parse({
      page: 1,
      page_size: 25,
      ...query,
    });
    const path = `/compliance-records${buildQueryString(parsedQuery)}`;
    return this.get(path, complianceRecordsListResponseSchema);
  }

  /**
   * `GET /compliance-records/:recordId/detail` — verification-screen
   * hydrate for the admin SPA (M13 verification workflow, US-ADM-03 /
   * US-ADM-04). Returns the record + slim person summary + full item
   * type row + (when evidence is uploaded) a freshly-presigned download
   * URL for the inline preview. 404 surfaces as `record_not_found`;
   * 401 short-circuits the SPA to the admin sign-in page.
   */
  async getAdminComplianceRecordDetail(recordId: string): Promise<ComplianceRecordDetailResponse> {
    return this.get(
      `/compliance-records/${encodeURIComponent(recordId)}/detail`,
      complianceRecordDetailResponseSchema,
    );
  }

  /**
   * `POST /compliance-records/:recordId/verify` — transactional verify
   * action. Callers supply verification notes (required), an optional
   * expiry-date override (for evidence-carried expiries like WWCC where
   * the expiry is on the screenshot rather than computed from the
   * completion date), the WWCC outcome (`valid` for non-WWCC flows), and
   * the prohibition-notice-check boolean (WWCC-only; `null` otherwise).
   * The server runs the entire status transition + expiry computation +
   * `people.updated_at` bump (drives the "needs NQA ITS sync" panel) +
   * audit row in one DB transaction. Branch on the stable error codes:
   *   - `invalid_status_transition` — record already verified / superseded
   *   - `evidence_required` — no evidence and no acknowledgement_signature
   *   - 404 `record_not_found`
   */
  async verifyAdminComplianceRecord(
    recordId: string,
    request: VerifyComplianceRecordRequest,
  ): Promise<VerifyComplianceRecordResponse> {
    const parsedRequest = verifyComplianceRecordRequestSchema.parse(request);
    return this.post(
      `/compliance-records/${encodeURIComponent(recordId)}/verify`,
      parsedRequest,
      verifyComplianceRecordResponseSchema,
    );
  }

  /**
   * `POST /compliance-records/:recordId/request-reverification` — the
   * WWCC help-panel hand-off for `Not Found` / name-mismatch / DOB-
   * mismatch outcomes (PRD 06 §WWCC help panel — unusual outcomes,
   * US-ADM-05). Keeps the record in `submitted` status, stamps the
   * reason + note into `verification_notes`, and
   * writes a paired audit row. The "email volunteer to reconfirm"
   * enqueue lands with the remaining M14 `Register-Mail` sites.
   *
   * Known error codes the UI branches on:
   *   - `invalid_status_transition` — record already verified / moved
   *   - 404 `record_not_found`
   */
  async requestAdminReverification(
    recordId: string,
    request: RequestReverificationRequest,
  ): Promise<RequestReverificationResponse> {
    const parsedRequest = requestReverificationRequestSchema.parse(request);
    return this.post(
      `/compliance-records/${encodeURIComponent(recordId)}/request-reverification`,
      parsedRequest,
      requestReverificationResponseSchema,
    );
  }

  /**
   * `POST /compliance-records/:recordId/supersede` — mark this record as
   * superseded by another (typically a renewal) record for the same
   * person + item type (PRD 03 §3, M13 — "supersede affordance inline
   * on the per-record list"). The server enforces same-person + same-
   * item-type + non-terminal replacement; the detail endpoint's
   * `supersede_candidates` array surfaces the valid replacement set so
   * the UI never asks the admin to pick a row that would 400.
   *
   * Known error codes the UI branches on:
   *   - `invalid_status_transition` — outgoing record not supersedable
   *   - `self_supersede` — replacement id equals the outgoing record id
   *   - `replacement_record_not_found` — replacement id does not exist
   *   - `replacement_cross_person` — replacement belongs to a different person
   *   - `replacement_cross_item_type` — replacement is for a different item type
   *   - `replacement_terminal` — replacement already superseded/archived/etc.
   *   - 404 `record_not_found`
   */
  async supersedeAdminComplianceRecord(
    recordId: string,
    request: SupersedeComplianceRecordRequest,
  ): Promise<ComplianceRecord> {
    const parsedRequest = supersedeComplianceRecordRequestSchema.parse(request);
    return this.post(
      `/compliance-records/${encodeURIComponent(recordId)}/supersede`,
      parsedRequest,
      complianceRecordSchema,
    );
  }

  /**
   * `POST /uploads/request-url` — admin-side request for a presigned PUT
   * URL (M5). The caller must already have `person_id` + `item_type_id`
   * resolved (the admin SPA reads them off the compliance-record detail
   * before calling this). Pair with `confirmAdminEvidenceUpload` and
   * `attachAdminEvidenceToRecord` to complete the "Upload on behalf"
   * flow (PRD 06 §Edge cases, M13).
   */
  async requestAdminEvidenceUploadUrl(
    request: RequestUploadUrlRequest,
  ): Promise<RequestUploadUrlResponse> {
    const parsedRequest = requestUploadUrlRequestSchema.parse(request);
    return this.post('/uploads/request-url', parsedRequest, requestUploadUrlResponseSchema);
  }

  /**
   * `POST /uploads/confirm` — admin-side confirm-after-PUT step (M5).
   * The server fetches the object, validates MIME + size, hashes the
   * bytes, inserts the `evidence` row with `uploaded_by_kind='admin'`,
   * and returns the new evidence id. The required `on_behalf_note`
   * lands in the audit row so the upload context is durable.
   */
  async confirmAdminEvidenceUpload(request: ConfirmUploadRequest): Promise<ConfirmUploadResponse> {
    const parsedRequest = confirmUploadRequestSchema.parse(request);
    return this.post('/uploads/confirm', parsedRequest, confirmUploadResponseSchema);
  }

  /**
   * `POST /compliance-records/:recordId/attach-evidence` — final step
   * of the admin "Upload on behalf" flow. Links an existing
   * `evidence` row onto a specific record and transitions the record
   * to `submitted` (PRD 06 §Edge cases, M13). The required
   * `on_behalf_note` lands in `verification_notes` so the next admin
   * verifying the record sees the upload context.
   *
   * Known error codes the UI branches on:
   *   - `invalid_status_transition` — record already verified / moved
   *   - `evidence_not_found` — evidence id doesn't belong to the
   *     record's (person, requirement)
   *   - 404 `record_not_found`
   */
  async attachAdminEvidenceToRecord(
    recordId: string,
    request: AttachEvidenceToRecordRequest,
  ): Promise<AttachEvidenceToRecordResponse> {
    const parsedRequest = attachEvidenceToRecordRequestSchema.parse(request);
    return this.post(
      `/compliance-records/${encodeURIComponent(recordId)}/attach-evidence`,
      parsedRequest,
      attachEvidenceToRecordResponseSchema,
    );
  }

  /**
   * `GET /admin/users` — paginated list of admin users. `page` +
   * `page_size` default to the server's (1, 50) if omitted.
   */
  async adminListUsers(query: Partial<PaginationParams> = {}): Promise<AdminUsersListResponse> {
    const path = `/admin/users${buildQueryString(query)}`;
    return this.get(path, adminUsersListResponseSchema);
  }

  /**
   * `GET /admin/users/:adminUserId` — single admin-user hydrate for the
   * detail / edit screen. 404 `admin_not_found` when the id is stale.
   */
  async getAdminUser(adminUserId: string): Promise<GetAdminUserResponse> {
    return this.get(`/admin/users/${encodeURIComponent(adminUserId)}`, getAdminUserResponseSchema);
  }

  /**
   * `POST /admin/users` — create a new admin. The new row lands with
   * `mfa_secret = NULL`; the admin signs in via the email-OTP and
   * lands on the forced TOTP-enrolment screen on first sign-in. There
   * is no password (admin auth is passwordless under the new shape).
   * 409 `admin_email_in_use` signals a duplicate email
   * (case-insensitive).
   */
  async adminCreateUser(request: CreateAdminUserRequest): Promise<CreateAdminUserResponse> {
    const parsedRequest = createAdminUserRequestSchema.parse(request);
    return this.post('/admin/users', parsedRequest, createAdminUserResponseSchema);
  }

  /**
   * `PATCH /admin/users/:adminUserId` — partial update of an admin user.
   * Accepts any subset of `{full_name, is_active}`; at least one field
   * is required. There is no `password` field — admin auth is
   * passwordless. Passing `is_active: false` deactivates the account —
   * the next sign-in fails with `admin_account_disabled` and any live
   * session for that user is rejected on the next middleware check.
   * 404 `admin_not_found` when the id is stale.
   */
  async adminUpdateUser(
    adminUserId: string,
    request: UpdateAdminUserRequest,
  ): Promise<UpdateAdminUserResponse> {
    const parsedRequest = updateAdminUserRequestSchema.parse(request);
    return this.patch(
      `/admin/users/${encodeURIComponent(adminUserId)}`,
      parsedRequest,
      updateAdminUserResponseSchema,
    );
  }

  /**
   * `POST /admin/users/:adminUserId/totp/enrol` — self-rotate. Scoped to
   * the actor's own row; rotating someone else's TOTP goes through
   * `adminResetTotp` (which requires a step-up TOTP from the actor).
   * Returns the `otpauth_uri` + base32 secret exactly once so the admin
   * can scan a QR code on a new device. 403 `totp_self_rotate_only` if
   * the path's id is not the actor's id.
   */
  async adminEnrolTotp(adminUserId: string): Promise<AdminTotpEnrolResponse> {
    const response = await this.requestRaw(
      'POST',
      `/admin/users/${encodeURIComponent(adminUserId)}/totp/enrol`,
      null,
    );
    const json = (await response.json()) as unknown;
    const parsed = adminTotpEnrolResponseSchema.safeParse(json);
    if (!parsed.success) {
      throw new ResponseValidationError(parsed.error.issues);
    }
    return parsed.data;
  }

  /**
   * `POST /admin/users/:adminUserId/totp/reset` — peer reset. The actor
   * clears another admin's TOTP secret so the affected admin can
   * re-enrol from scratch on a new device. Requires
   * the actor's current 6-digit TOTP code as a step-up so a stolen
   * session can't silently rotate every peer's second factor. The
   * affected admin's next sign-in lands on the forced enrolment
   * screen.
   *
   * Known error codes the UI branches on:
   *   - 400 `totp_self_reset_forbidden` — actor tried to reset their
   *     own TOTP through this endpoint (use the bootstrap CLI flag
   *     `--reset-totp` on the host instead).
   *   - 400 `step_up_totp_failed` — the actor's TOTP code didn't verify.
   *   - 404 `admin_not_found` — stale admin id.
   */
  async adminResetTotp(
    adminUserId: string,
    request: AdminTotpResetRequest,
  ): Promise<AdminTotpResetResponse> {
    const parsedRequest = adminTotpResetRequestSchema.parse(request);
    return this.post(
      `/admin/users/${encodeURIComponent(adminUserId)}/totp/reset`,
      parsedRequest,
      adminTotpResetResponseSchema,
    );
  }

  /**
   * `GET /compliance-requirements` — the compliance-requirements config
   * list for the admin SPA (M7, M13 config surface). Every authenticated
   * admin can read and edit; the optional `active` filter maps straight
   * through to the server's query param.
   */
  async adminListComplianceRequirements(
    query: { active?: boolean } = {},
  ): Promise<ComplianceRequirementsListResponse> {
    const path = `/compliance-requirements${buildQueryString(query)}`;
    return this.get(path, complianceRequirementsListResponseSchema);
  }

  /**
   * `GET /compliance-requirements/:requirementId` — single-row hydrate
   * for the admin SPA detail / edit screen (M13). 404
   * `requirement_not_found` when the id is stale.
   */
  async adminGetComplianceRequirement(requirementId: string): Promise<ComplianceRequirement> {
    return this.get(
      `/compliance-requirements/${encodeURIComponent(requirementId)}`,
      complianceRequirementSchema,
    );
  }

  /**
   * `POST /compliance-requirements` — create a new requirement.
   * 409 `short_code_in_use` when the `short_code` is already taken
   * (case-sensitive string match).
   */
  async adminCreateComplianceRequirement(
    request: CreateComplianceRequirementRequest,
  ): Promise<ComplianceRequirement> {
    const parsedRequest = createComplianceRequirementRequestSchema.parse(request);
    return this.post('/compliance-requirements', parsedRequest, complianceRequirementSchema);
  }

  /**
   * `PATCH /compliance-requirements/:requirementId` — partial update.
   * At least one field is required or the server rejects with
   * validation_failed. Passing `active: false` is how an admin retires a
   * seeded requirement — existing records aren't touched, but the
   * requirement no longer surfaces on `/me` / intake.
   */
  async adminPatchComplianceRequirement(
    requirementId: string,
    request: UpdateComplianceRequirementRequest,
  ): Promise<ComplianceRequirement> {
    const parsedRequest = updateComplianceRequirementRequestSchema.parse(request);
    return this.patch(
      `/compliance-requirements/${encodeURIComponent(requirementId)}`,
      parsedRequest,
      complianceRequirementSchema,
    );
  }

  /**
   * `POST /compliance-requirements/:requirementId/bulk-invalidate` —
   * admin-triggered bulk invalidation of every currently-verified record
   * for the requirement (M10 rework). Replaces the implicit
   * auto-expire-on-document-version-bump that the old
   * `fixed_review_cycle` strategy provided. `expected_count` is the
   * race-protection knob: if the actual row count drifted between page
   * load and click the server returns 409 so the admin re-confirms.
   */
  async adminBulkInvalidateRequirement(
    requirementId: string,
    body: BulkInvalidateRequirementRequest,
  ): Promise<BulkInvalidateRequirementResponse> {
    const parsedRequest = bulkInvalidateRequirementRequestSchema.parse(body);
    return this.post(
      `/compliance-requirements/${encodeURIComponent(requirementId)}/bulk-invalidate`,
      parsedRequest,
      bulkInvalidateRequirementResponseSchema,
    );
  }

  /**
   * `GET /admin/worker-jobs/health` — cron health card data (M9 worker
   * simplification + EventBridge trigger refactor): one row per
   * scheduled job, last successful tick, staleness flag.
   */
  async getAdminCronHealth(): Promise<CronHealthResponse> {
    return this.get('/admin/worker-jobs/health', cronHealthResponseSchema);
  }

  /**
   * `GET /admin/worker-jobs/mail-failures` — list of unretried-mail-failure
   * rows. Per-row Retry button on the SPA hits the endpoint below.
   */
  async getAdminMailFailures(): Promise<MailFailuresListResponse> {
    return this.get('/admin/worker-jobs/mail-failures', mailFailuresListResponseSchema);
  }

  /**
   * `POST /admin/worker-jobs/mail-failures/:failureId/retry` — re-send
   * the captured envelope from a `mail_failures` row.
   */
  async retryAdminMailFailure(failureId: string): Promise<MailFailureRetryResponse> {
    const response = await this.requestRaw(
      'POST',
      `/admin/worker-jobs/mail-failures/${encodeURIComponent(failureId)}/retry`,
      null,
    );
    const json = (await response.json()) as unknown;
    const parsed = mailFailureRetryResponseSchema.safeParse(json);
    if (!parsed.success) {
      throw new ResponseValidationError(parsed.error.issues);
    }
    return parsed.data;
  }

  /**
   * `DELETE /admin/worker-jobs/mail-failures/:failureId` — hard-delete a
   * mail-failure row. Used when retrying would never succeed (known sender
   * misconfiguration etc.) and the row is just panel noise.
   */
  async deleteAdminMailFailure(failureId: string): Promise<MailFailureDeleteResponse> {
    const response = await this.requestRaw(
      'DELETE',
      `/admin/worker-jobs/mail-failures/${encodeURIComponent(failureId)}`,
      null,
    );
    const json = (await response.json()) as unknown;
    const parsed = mailFailureDeleteResponseSchema.safeParse(json);
    if (!parsed.success) {
      throw new ResponseValidationError(parsed.error.issues);
    }
    return parsed.data;
  }

  /**
   * `GET /admin/worker-jobs/audit-packs` — stuck audit pack runs (failed
   * or pending past expected duration).
   */
  async getAdminStuckAuditPacks(): Promise<StuckAuditPacksResponse> {
    return this.get('/admin/worker-jobs/audit-packs', stuckAuditPacksResponseSchema);
  }

  /**
   * `POST /admin/worker-jobs/cron/:jobName/run` — admin-session-authed
   * trigger that fires the same cron handler the EventBridge schedule
   * does. Idempotent inside the same Sydney bucket: a manual run after
   * the scheduled tick has already landed returns `idempotent_skip`.
   */
  async runAdminCronJob(jobName: 'daily_reminder' | 'weekly_digest'): Promise<CronTriggerResponse> {
    const response = await this.requestRaw(
      'POST',
      `/admin/worker-jobs/cron/${encodeURIComponent(jobName)}/run`,
      null,
    );
    const json = (await response.json()) as unknown;
    const parsed = cronTriggerResponseSchema.safeParse(json);
    if (!parsed.success) {
      throw new ResponseValidationError(parsed.error.issues);
    }
    return parsed.data;
  }

  /**
   * `POST /people/:personId/nqa-its-sync` — admin marks the person as
   * up-to-date in the ACECQA NQA ITS portal after manually completing
   * the round-trip. Stamps `people.nqa_its_synced_at = now()` and
   * (optionally) captures the freshly-issued `nqa_its_entry_id`. The
   * dashboard's "needs sync" panel re-derives off this timestamp.
   */
  async markPersonNqaItsSynced(
    personId: string,
    request: MarkNqaItsSyncedRequest = {},
  ): Promise<MarkNqaItsSyncedResponse> {
    const parsedRequest = markNqaItsSyncedRequestSchema.parse(request);
    return this.post(
      `/people/${encodeURIComponent(personId)}/nqa-its-sync`,
      parsedRequest,
      markNqaItsSyncedResponseSchema,
    );
  }

  /**
   * `GET /admin/documents` — paginated list of policy documents.
   * By default only active rows are returned; pass `include_archived=true`
   * to surface retired versions too.
   */
  async adminListDocuments(
    query: Partial<DocumentsListQuery> = {},
  ): Promise<DocumentsListResponse> {
    const parsed = documentsListQuerySchema.parse({
      page: 1,
      page_size: 25,
      include_archived: false,
      ...query,
    });
    const path = `/admin/documents${buildQueryString(parsed)}`;
    return this.get(path, documentsListResponseSchema);
  }

  /**
   * `POST /admin/documents/upload-url` — step 1 of the admin policy-upload
   * flow (US-ADM-07). The caller hands the server the filename + size; the
   * server returns a short-lived presigned PUT URL into
   * `documents/rendered/{uuid}.pdf`. Step 2 is a direct browser PUT to that
   * URL with the `.pdf` bytes; step 3 is `adminCreateDocument` which
   * takes the returned `storage_key` and metadata and renders the PDF.
   * Only `.pdf` extensions are accepted server-side.
   */
  async requestAdminDocumentUploadUrl(
    request: RequestDocumentUploadUrlRequest,
  ): Promise<RequestDocumentUploadUrlResponse> {
    const parsedRequest = requestDocumentUploadUrlRequestSchema.parse(request);
    return this.post(
      '/admin/documents/upload-url',
      parsedRequest,
      requestDocumentUploadUrlResponseSchema,
    );
  }

  /**
   * PUTs a `.pdf` file at the presigned URL returned by
   * `requestAdminDocumentUploadUrl`. The S3/MinIO endpoint returns 200 on
   * success with no JSON envelope, so the method resolves to void. On any
   * non-2xx we throw a plain `Error` — the UI surfaces it as "upload
   * failed" without trying to parse an API error envelope (the bucket
   * speaks XML, not our JSON shape).
   */
  async putDocumentFile(uploadUrl: string, file: File): Promise<void> {
    // Server presigns the PUT against `application/pdf` (the only MIME the
    // documents flow accepts). The Content-Type header on the PUT must
    // match the signed Content-Type, so we set it explicitly rather than
    // trusting `file.type` (browsers occasionally serve `application/pdf`
    // as `application/octet-stream` or empty). Sending the wrong type
    // also leaves MinIO storing the wrong Content-Type metadata, which
    // makes the volunteer's iframe-render fail and the download arrive
    // with the wrong extension.
    const response = await this.fetchImpl(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': 'application/pdf' },
    });
    if (!response.ok) {
      throw new Error(`Upload failed with status ${response.status}.`);
    }
  }

  /**
   * `POST /admin/documents` — one-shot create. The admin uploads the PDF
   * via `requestAdminDocumentUploadUrl` first, then calls this with the
   * returned `storage_key` plus `title` and `version`. The server hashes
   * the bytes and INSERTs a fully-formed active row — there is no
   * separate publish step.
   * 409 `document_version_exists` signals a duplicate `(title, version)`.
   */
  async adminCreateDocument(request: CreateDocumentRequest): Promise<Document> {
    const parsedRequest = createDocumentRequestSchema.parse(request);
    return this.post('/admin/documents', parsedRequest, documentSchema);
  }

  /**
   * `POST /admin/documents/:id/archive` — retire a no-longer-current
   * version. Idempotent. Refuses with 409 `document_in_use_by_requirement`
   * when an active requirement still links to the row — operator must
   * repoint the requirement first.
   */
  async adminArchiveDocument(documentId: string): Promise<ArchiveDocumentResponse> {
    return this.post(
      `/admin/documents/${encodeURIComponent(documentId)}/archive`,
      {},
      archiveDocumentResponseSchema,
    );
  }

  /**
   * `POST /admin/documents/:id/unarchive` — restore a previously-archived
   * row. Idempotent if not currently archived.
   */
  async adminUnarchiveDocument(documentId: string): Promise<UnarchiveDocumentResponse> {
    return this.post(
      `/admin/documents/${encodeURIComponent(documentId)}/unarchive`,
      {},
      unarchiveDocumentResponseSchema,
    );
  }

  /**
   * `DELETE /admin/documents/:id` — hard-delete a row that has no
   * signatures yet. Returns 204 on success. After at least one signature
   * exists the row is referentially load-bearing forever; the admin must
   * archive instead.
   */
  async adminDeleteDocument(documentId: string): Promise<void> {
    await this.requestRaw('DELETE', `/admin/documents/${encodeURIComponent(documentId)}`, null);
  }

  /**
   * `GET /admin/documents/:id/pdf-url` — presigned read URL for the rendered
   * PDF. Short expiry (5 min) — admins click through to view the file.
   * 404 `pdf_not_available` means the render never ran or the row is
   * malformed. Failure copy in the UI steers admins to the draft-upload
   * path rather than showing a raw error.
   */
  async getAdminDocumentPdfUrl(documentId: string): Promise<DocumentPdfUrlResponse> {
    return this.get(
      `/admin/documents/${encodeURIComponent(documentId)}/pdf-url`,
      documentPdfUrlResponseSchema,
    );
  }

  /**
   * `POST /admin/exports` — enqueue a regulator audit-pack build
   * (US-ADM-14 / US-REG-01). The server returns 202 with a pending
   * job id; the admin keeps working while the worker builds the
   * `.zip` with workbooks + evidence + manifest + summary.txt. The
   * "Recent exports" list re-renders to surface the row, and the
   * admin gets an email pointing at the in-app download endpoint
   * once the build completes. Filters default to "whole register" —
   * an empty request means every audit row, every person, every
   * item type.
   */
  async requestAuditPack(request: ExportAuditPackRequest): Promise<ExportAuditPackResponse> {
    const parsedRequest = exportAuditPackRequestSchema.parse(request);
    return this.post('/admin/exports', parsedRequest, exportAuditPackResponseSchema);
  }

  /**
   * `GET /admin/exports` — Recent exports list (up to 50, newest first).
   * Each row carries a stable login-gated `download_path`; the SPA
   * renders it as a same-origin `<a>` so the browser sends the admin
   * session cookie automatically.
   */
  async listAdminExports(): Promise<ExportListResponse> {
    return this.get('/admin/exports', exportListResponseSchema);
  }

  /**
   * `GET /me/acknowledgements` — list the volunteer's pending policy
   * acknowledgements (M13). Same shape as the `pending_acknowledgements`
   * field on `meDashboardResponseSchema`; surfaced separately so the
   * SPA can re-poll the pane after a sign without re-fetching the
   * whole dashboard payload.
   */
  async listMePendingAcks(): Promise<MePendingAckListResponse> {
    return this.get('/me/acknowledgements', mePendingAckListResponseSchema);
  }

  /**
   * `POST /me/ack/start` — open the inline read pane for one pending
   * policy ack (M13). Returns the rendered PDF URL, the verbatim
   * acknowledgement statement, and a fresh server-side `page_render_id`
   * nonce that the subsequent `/me/ack/step-up` call must echo back.
   */
  async startMeAck(request: MeAckStartRequest): Promise<MeAckStartResponse> {
    const parsedRequest = meAckStartRequestSchema.parse(request);
    return this.post('/me/ack/start', parsedRequest, meAckStartResponseSchema);
  }

  /**
   * `POST /me/ack/step-up` — validate the typed name + mint and email
   * a fresh 6-digit confirmation code (M13). Returns the `attempt_id`
   * the SPA echoes on the confirm call. The code is sent inline; a
   * persistent SMTP failure surfaces as 502 `step_up_mail_failed`.
   *
   * Known error codes the UI branches on:
   *   - `name_mismatch` (400) — typed name cannot be reconciled with
   *     the name on file; fall back to the wet-signature hand-off copy.
   *   - `name_near_match_needs_override` (400) — typed name close but
   *     not exact; UI prompts for override and resubmits with
   *     `confirmations.name_override = true`.
   *   - `page_render_mismatch` (400) — a stale tab tried to step-up
   *     against a newer start; UI offers a "reload" prompt.
   *   - `step_up_mail_failed` (502) — the code was minted but couldn't
   *     be delivered; UI tells the volunteer to try again.
   */
  async stepUpMeAck(request: MeAckStepUpRequest): Promise<MeAckStepUpResponse> {
    const parsedRequest = meAckStepUpRequestSchema.parse(request);
    return this.post('/me/ack/step-up', parsedRequest, meAckStepUpResponseSchema);
  }

  /**
   * `POST /me/ack/confirm` — atomic redeem-and-sign (M13). Validates
   * the 6-digit code, marks the `magic_link_tokens` row used, writes
   * the `acknowledgement_signatures` row, transitions the compliance
   * record to `verified`, and writes the paired audit row — all in
   * one DB transaction. All code-failure modes collapse to
   * `invalid_code` (401).
   */
  async confirmMeAck(request: MeAckConfirmRequest): Promise<MeAckConfirmResponse> {
    const parsedRequest = meAckConfirmRequestSchema.parse(request);
    return this.post('/me/ack/confirm', parsedRequest, meAckConfirmResponseSchema);
  }

  /**
   * `POST /me/intake` — volunteer fills in their identity / contact /
   * NQA details on the `/me` dashboard. Replaces the retired
   * `/intake/:token` flow. Authenticated via the volunteer session
   * cookie; gated to `status='onboarding'` so a fully-active volunteer
   * doesn't accidentally re-submit (the dedicated PATCH path is the
   * route for that — admin-only).
   */
  async submitMeIntake(request: MeIntakeSubmitRequest): Promise<MeIntakeSubmitResponse> {
    const parsedRequest = meIntakeSubmitRequestSchema.parse(request);
    return this.post('/me/intake', parsedRequest, meIntakeSubmitResponseSchema);
  }

  private async post<T>(path: string, body: unknown, responseSchema: z.ZodType<T>): Promise<T> {
    const response = await this.requestRaw('POST', path, body);
    const json = (await response.json()) as unknown;
    const parsed = responseSchema.safeParse(json);
    if (!parsed.success) {
      throw new ResponseValidationError(parsed.error.issues);
    }
    return parsed.data;
  }

  private async patch<T>(path: string, body: unknown, responseSchema: z.ZodType<T>): Promise<T> {
    const response = await this.requestRaw('PATCH', path, body);
    const json = (await response.json()) as unknown;
    const parsed = responseSchema.safeParse(json);
    if (!parsed.success) {
      throw new ResponseValidationError(parsed.error.issues);
    }
    return parsed.data;
  }

  private async get<T>(path: string, responseSchema: z.ZodType<T>): Promise<T> {
    const response = await this.requestRaw('GET', path, null);
    const json = (await response.json()) as unknown;
    const parsed = responseSchema.safeParse(json);
    if (!parsed.success) {
      throw new ResponseValidationError(parsed.error.issues);
    }
    return parsed.data;
  }

  private async put<T>(path: string, body: unknown, responseSchema: z.ZodType<T>): Promise<T> {
    const response = await this.requestRaw('PUT', path, body);
    const json = (await response.json()) as unknown;
    const parsed = responseSchema.safeParse(json);
    if (!parsed.success) {
      throw new ResponseValidationError(parsed.error.issues);
    }
    return parsed.data;
  }

  private async del<T>(path: string, responseSchema: z.ZodType<T>): Promise<T> {
    const response = await this.requestRaw('DELETE', path, null);
    const json = (await response.json()) as unknown;
    const parsed = responseSchema.safeParse(json);
    if (!parsed.success) {
      throw new ResponseValidationError(parsed.error.issues);
    }
    return parsed.data;
  }

  private async requestRaw(method: string, path: string, body: unknown): Promise<Response> {
    const url = `${this.baseUrl}${path}`;
    const init: RequestInit = {
      method,
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...(body !== null ? { 'Content-Type': 'application/json' } : {}),
      },
      ...(body !== null ? { body: JSON.stringify(body) } : {}),
    };

    const response = await this.fetchImpl(url, init);

    if (!response.ok) {
      const rawText = await response.text();
      let parsedEnvelope: ApiErrorResponse;
      try {
        parsedEnvelope = apiErrorResponseSchema.parse(JSON.parse(rawText));
      } catch {
        parsedEnvelope = {
          error: {
            code: 'network_error',
            message: rawText || `Request failed with status ${response.status}`,
            details: null,
          },
          request_id: response.headers.get('x-request-id') ?? 'unknown',
        };
      }
      throw new ApiError(response.status, parsedEnvelope);
    }

    return response;
  }
}

/**
 * Serialise a flat object of query params into a `?foo=bar&baz=1` string.
 * Values that are `undefined` or `null` are skipped — so passing
 * `{ status: undefined }` sends no `status` param at all rather than
 * `status=` (which zod's coerce would reject for enum columns). Booleans
 * and numbers are stringified. Empty strings are also skipped for the
 * same reason — `q=` is not a useful filter value.
 */
function buildQueryString(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.append(key, String(value));
  }
  const out = search.toString();
  return out ? `?${out}` : '';
}

/** Module-level default client — used by routes via `import { apiClient }`.
 *  Overridable for tests by constructing a fresh `ApiClient`. */
export const apiClient = new ApiClient();
